import { useState, useEffect, useRef } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useTranslation } from 'react-i18next';
import axios from 'axios';
import ModuleLayout from '../components/ModuleLayout';
import SitePreview from '../components/SitePreview';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Dialog, DialogContent } from '../components/ui/dialog';
import { Switch } from '../components/ui/switch';
import {
  Wand2, Loader2, Globe, Link2,
  Send, Check, RefreshCw, Save, Eye, ChevronRight,
  ImagePlus, Trash2, ExternalLink, Monitor, Smartphone, Tablet,
  Sparkles, Moon, Sun, Square, Circle, Database, PenLine, Layers, CheckCircle2,
  Copy, Palette, Type, Image as ImageIcon, ArrowLeft
} from 'lucide-react';
import { toast } from 'sonner';
import { getTemplateCategory } from '../config/businessTypes';

const FONT_OPTIONS = [
  'Cairo', 'IBM Plex Sans Arabic', 'Playfair Display', 'Tajawal', 'Almarai', 'Noto Sans Arabic'
];

const GEN_STEPS_KEYS = [
  { key: 'analyzing', icon: Database, labelKey: 'builder.genStep_analyzing' },
  { key: 'writing', icon: PenLine, labelKey: 'builder.genStep_writing' },
  { key: 'designing', icon: Layers, labelKey: 'builder.genStep_designing' },
  { key: 'imaging', icon: Sparkles, labelKey: 'builder.genStep_imaging' },
  { key: 'finishing', icon: Sparkles, labelKey: 'builder.genStep_finishing' },
];

/* ═══════ GENERATION PROGRESS OVERLAY ═══════ */
function GenerationProgress({ phase }) {
  const { t } = useTranslation();
  const GEN_STEPS = GEN_STEPS_KEYS.map(s => ({ ...s, label: t(s.labelKey) }));
  const stepIndex = GEN_STEPS.findIndex(s => s.key === phase);
  const progress = phase === 'done' ? 100 : Math.min(95, ((stepIndex + 1) / GEN_STEPS.length) * 90 + 5);

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center" style={{ backgroundColor: 'rgba(0,0,0,0.6)', backdropFilter: 'blur(8px)' }} data-testid="generation-overlay">
      <div className="bg-card rounded-2xl p-8 w-full max-w-md mx-4 shadow-2xl border border-border/50" dir="rtl">
        <div className="text-center mb-8">
          <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-primary/10 flex items-center justify-center">
            <Wand2 className="w-8 h-8 text-primary animate-pulse" />
          </div>
          <h3 className="font-cairo font-bold text-xl text-foreground mb-1">{t('builder.genTitle')}</h3>
          <p className="text-sm text-muted-foreground">{t('builder.genDesc')}</p>
        </div>

        <div className="space-y-4 mb-8">
          {GEN_STEPS.map((step, i) => {
            const Icon = step.icon;
            const isActive = step.key === phase;
            const isDone = i < stepIndex || phase === 'done';
            return (
              <div key={step.key} className={`flex items-center gap-3 transition-all duration-500 ${isActive ? 'opacity-100' : isDone ? 'opacity-60' : 'opacity-30'}`} data-testid={`gen-step-${step.key}`}>
                <div className={`w-9 h-9 rounded-full flex items-center justify-center transition-all duration-500 ${isDone ? 'bg-green-500/20' : isActive ? 'bg-primary/20' : 'bg-muted'}`}>
                  {isDone ? <CheckCircle2 className="w-5 h-5 text-green-500" /> : isActive ? <Icon className="w-5 h-5 text-primary animate-pulse" /> : <Icon className="w-5 h-5 text-muted-foreground" />}
                </div>
                <span className={`text-sm font-medium ${isActive ? 'text-foreground' : isDone ? 'text-muted-foreground' : 'text-muted-foreground/50'}`}>{step.label}</span>
                {isDone && <Check className="w-4 h-4 text-green-500 mr-auto" />}
              </div>
            );
          })}
        </div>

        {/* Progress bar */}
        <div className="h-2 bg-muted rounded-full overflow-hidden">
          <div className="h-full bg-primary rounded-full transition-all duration-1000 ease-out" style={{ width: `${progress}%` }} data-testid="gen-progress-bar" />
        </div>
        <p className="text-xs text-muted-foreground text-center mt-3">جاري توليد موقعك مع صور مخصصة بالذكاء الاصطناعي...</p>
      </div>
    </div>
  );
}

/* ═══════ DEVICE FRAME FOR MOBILE PREVIEW ═══════ */
function PhoneFrame({ children }) {
  return (
    <div className="flex items-start justify-center h-full pt-4 pb-4 overflow-hidden" style={{ backgroundColor: '#f5f5f5' }}>
      <div style={{ width: 375, height: 'calc(100% - 16px)', borderRadius: 40, border: '6px solid #1a1a1a', overflow: 'hidden', boxShadow: '0 20px 60px rgba(0,0,0,0.3)', position: 'relative', background: '#000' }}>
        {/* Notch */}
        <div style={{ position: 'absolute', top: 0, left: '50%', transform: 'translateX(-50%)', width: 120, height: 28, backgroundColor: '#1a1a1a', borderRadius: '0 0 16px 16px', zIndex: 10 }} />
        <div style={{ height: '100%', overflow: 'hidden', borderRadius: 34 }}>
          {children}
        </div>
      </div>
    </div>
  );
}

/* ═══════ MAIN COMPONENT ═══════ */
export default function AIBuilderPage() {
  const { authHeaders, API, activeBranch } = useAuth();
  const { t } = useTranslation();

  // Core states
  const [step, setStep] = useState('start');
  const [templates, setTemplates] = useState([]);
  const [selectedTemplate, setSelectedTemplate] = useState('modern');
  const [prompt, setPrompt] = useState('');
  const [urlInput, setUrlInput] = useState('');
  const [urlContent, setUrlContent] = useState('');
  const [siteConfig, setSiteConfig] = useState(null);
  const [menuItems, setMenuItems] = useState([]);
  const [uploadedImages, setUploadedImages] = useState([]);
  const [generating, setGenerating] = useState(false);
  const [analyzing, setAnalyzing] = useState(false);
  const [saving, setSaving] = useState(false);
  const [siteId, setSiteId] = useState(null);
  const [showChatWidget, setShowChatWidget] = useState(true);
  const [savedSites, setSavedSites] = useState([]);
  const [chatMessages, setChatMessages] = useState([]);
  const [chatInput, setChatInput] = useState('');
  const [cloneAnalysis, setCloneAnalysis] = useState(null);  const [chatLoading, setChatLoading] = useState(false);
  const [previewFullscreen, setPreviewFullscreen] = useState(false);
  const [builderContext, setBuilderContext] = useState(null);

  // NEW: UX Enhancement states
  const [previewMode, setPreviewMode] = useState('desktop');
  const [generationPhase, setGenerationPhase] = useState(null);
  const [previewToken, setPreviewToken] = useState(null);

  const chatEndRef = useRef(null);
  const fileInputRef = useRef(null);
  const phaseTimerRef = useRef(null);

  useEffect(() => {
    fetchTemplates();
    fetchSavedSites();
    fetchBuilderContext();
    if (activeBranch) fetchMenuItems();
  }, [activeBranch]);

  useEffect(() => { chatEndRef.current?.scrollIntoView({ behavior: 'smooth' }); }, [chatMessages]);

  // Cleanup phase timer
  useEffect(() => () => { if (phaseTimerRef.current) clearInterval(phaseTimerRef.current); }, []);

  const fetchBuilderContext = async () => {
    try {
      const res = await axios.get(`${API}/builder/context`, authHeaders());
      setBuilderContext(res.data);
      const tmplCat = getTemplateCategory(res.data.business_type);
      if (tmplCat === 'cafe') setSelectedTemplate('cozy');
      else if (tmplCat === 'food_truck') setSelectedTemplate('vibrant');
    } catch (err) { console.error(err); }
  };

  const fetchTemplates = async () => {
    try {
      const res = await axios.get(`${API}/builder/templates`, authHeaders());
      setTemplates(res.data);
    } catch (err) { console.error(err); }
  };

  const fetchMenuItems = async () => {
    if (!activeBranch) return;
    try {
      const res = await axios.get(`${API}/menu-items?branch_id=${activeBranch.id}`, authHeaders());
      setMenuItems(res.data);
    } catch (err) { console.error(err); }
  };

  const fetchSavedSites = async () => {
    try {
      const res = await axios.get(`${API}/builder/sites`, authHeaders());
      setSavedSites(res.data);
    } catch (err) { console.error(err); }
  };

  const sortedTemplates = [...templates].sort((a, b) => {
    const bt = getTemplateCategory(builderContext?.business_type || 'restaurant');
    if (a.business_type === bt && b.business_type !== bt) return -1;
    if (a.business_type !== bt && b.business_type === bt) return 1;
    return 0;
  });

  /* ═══ GENERATION PROGRESS SIMULATION ═══ */
  const startPhaseAnimation = () => {
    setGenerationPhase('analyzing');
    const phases = ['analyzing', 'writing', 'designing', 'imaging', 'finishing'];
    let idx = 0;
    phaseTimerRef.current = setInterval(() => {
      idx++;
      if (idx < phases.length) {
        setGenerationPhase(phases[idx]);
      } else {
        clearInterval(phaseTimerRef.current);
      }
    }, 8000);
  };

  const stopPhaseAnimation = () => {
    if (phaseTimerRef.current) clearInterval(phaseTimerRef.current);
    setGenerationPhase('done');
    setTimeout(() => setGenerationPhase(null), 800);
  };

  /* ═══ ONE-CLICK GENERATION ═══ */
  const handleOneClickGenerate = () => {
    const ctx = builderContext;
    if (!ctx) return;
    const typeLabel = getTemplateCategory(ctx.business_type) === 'cafe' ? 'كافيه' : getTemplateCategory(ctx.business_type) === 'food_truck' ? 'عربة طعام' : 'مطعم';
    const autoPrompt = `أنشئ موقع احترافي مبهر لـ "${ctx.branch_name || 'مطعمي'}" وهو ${typeLabel} في ${ctx.branch_address || 'السعودية'}. استخدم بيانات المنيو والفرع المتاحة لديك.`;
    handleGenerate(autoPrompt);
  };

  /* ═══ GENERATE ═══ */
  const handleGenerate = async (autoPrompt) => {
    const actualPrompt = autoPrompt || prompt.trim();
    if (!actualPrompt && !urlContent) {
      toast.error(t('builder.enterDescOrUrl'));
      return;
    }
    setGenerating(true);
    startPhaseAnimation();
    try {
      const res = await axios.post(`${API}/builder/generate`, {
        template_id: selectedTemplate,
        prompt: actualPrompt,
        url_content: urlContent,
        branch_id: activeBranch?.id || ''
      }, authHeaders());
      if (res.data.success && res.data.config) {
        const cfg = res.data.config;
        if (uploadedImages.length > 0) {
          cfg.gallery_images = [...(cfg.gallery_images || []), ...uploadedImages.map(img => img.url)];
        }
        cfg.show_chat_widget = showChatWidget;
        setSiteConfig(cfg);
        stopPhaseAnimation();
        setTimeout(() => {
          setStep('customize');
          setChatMessages([{ role: 'assistant', content: t('builder.chatCreated') }]);
          toast.success(t('builder.siteCreated'));
        }, 900);
      }
    } catch (err) {
      stopPhaseAnimation();
      toast.error(t('builder.createFailed'));
    } finally {
      setGenerating(false);
    }
  };

  const handleAnalyzeUrl = async () => {
    if (!urlInput.trim()) { toast.error(t('builder.enterUrl')); return; }
    setAnalyzing(true);
    setCloneAnalysis(null);
    try {
      const res = await axios.post(`${API}/builder/analyze-url`, { url: urlInput.trim() }, authHeaders());
      if (res.data.success && res.data.data) {
        const d = res.data.data;
        setCloneAnalysis(d);
        setUrlContent(JSON.stringify(d));
        toast.success('تم تحليل الموقع بنجاح!');
      } else {
        toast.error(res.data.error || t('builder.urlAnalyzeFailed'));
      }
    } catch (err) { toast.error(t('builder.urlAccessFailed')); }
    finally { setAnalyzing(false); }
  };

  const handleCloneGenerate = async () => {
    if (!cloneAnalysis) { toast.error('حلّل الموقع أولاً'); return; }
    setGenerating(true);
    startPhaseAnimation();
    try {
      const res = await axios.post(`${API}/builder/clone`, {
        template_id: selectedTemplate,
        prompt: cloneAnalysis.title || '',
        url_content: JSON.stringify(cloneAnalysis),
        branch_id: activeBranch?.id || ''
      }, authHeaders());
      if (res.data.success && res.data.config) {
        const cfg = res.data.config;
        cfg.show_chat_widget = showChatWidget;
        setSiteConfig(cfg);
        stopPhaseAnimation();
        setTimeout(() => {
          setStep('customize');
          setChatMessages([{ role: 'assistant', content: 'تم استنساخ أسلوب الموقع بنجاح! بيانات فرعك ومنيوك تم دمجها تلقائياً. يمكنك تعديل أي شيء عبر المحادثة.' }]);
          toast.success('تم استنساخ الموقع بنجاح!');
        }, 900);
      }
    } catch (err) {
      stopPhaseAnimation();
      toast.error('فشل الاستنساخ');
    } finally { setGenerating(false); }
  };

  const handleImageUpload = async (e) => {
    const files = Array.from(e.target.files);
    for (const file of files) {
      if (file.size > 5 * 1024 * 1024) { toast.error(`${file.name} ${t('builder.fileTooLarge')}`); continue; }
      try {
        const formData = new FormData();
        formData.append('file', file);
        const res = await axios.post(`${API}/builder/upload-image`, formData, {
          ...authHeaders(), headers: { ...authHeaders().headers, 'Content-Type': 'multipart/form-data' }
        });
        setUploadedImages(prev => [...prev, { ...res.data, fullUrl: `${API.replace('/api', '')}${res.data.url}` }]);
        toast.success(`${t('builder.uploaded')} ${file.name}`);
      } catch (err) { toast.error(`${t('builder.uploadFailed')} ${file.name}`); }
    }
    e.target.value = '';
  };

  const removeImage = (idx) => setUploadedImages(prev => prev.filter((_, i) => i !== idx));

  const handleChatModify = async () => {
    if (!chatInput.trim() || chatLoading) return;
    const msg = chatInput.trim();
    setChatInput('');
    setChatMessages(prev => [...prev, { role: 'user', content: msg }]);
    setChatLoading(true);
    try {
      const res = await axios.post(`${API}/builder/modify`, { site_id: siteId || '', message: msg, current_config: siteConfig }, authHeaders());
      if (res.data.success && res.data.config) {
        setSiteConfig(res.data.config);
        setChatMessages(prev => [...prev, { role: 'assistant', content: res.data.message || t('builder.chatModified') }]);
      } else {
        setChatMessages(prev => [...prev, { role: 'assistant', content: res.data.message || t('builder.chatModifyFailed') }]);
      }
    } catch (err) {
      setChatMessages(prev => [...prev, { role: 'assistant', content: t('builder.chatError') }]);
    } finally { setChatLoading(false); }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      if (siteId) {
        await axios.put(`${API}/builder/sites/${siteId}`, { name: siteConfig.restaurant_name || 'My Site', branch_id: activeBranch?.id || '', config: siteConfig, is_published: false }, authHeaders());
      } else {
        const res = await axios.post(`${API}/builder/sites`, { name: siteConfig.restaurant_name || 'My Site', branch_id: activeBranch?.id || '', template_id: selectedTemplate, config: siteConfig, is_published: false }, authHeaders());
        setSiteId(res.data.id);
        setPreviewToken(res.data.preview_token);
      }
      toast.success(t('builder.saved'));
      fetchSavedSites();
    } catch (err) { toast.error(t('builder.saveFailed')); }
    finally { setSaving(false); }
  };

  const handlePublish = async () => {
    setSaving(true);
    try {
      let id = siteId;
      if (!id) {
        const res = await axios.post(`${API}/builder/sites`, { name: siteConfig.restaurant_name || 'My Site', branch_id: activeBranch?.id || '', template_id: selectedTemplate, config: siteConfig, is_published: true }, authHeaders());
        id = res.data.id;
        setSiteId(id);
        setPreviewToken(res.data.preview_token);
      } else {
        await axios.post(`${API}/builder/sites/${id}/publish`, {}, authHeaders());
        await axios.put(`${API}/builder/sites/${id}`, { name: siteConfig.restaurant_name || 'My Site', branch_id: activeBranch?.id || '', config: siteConfig, is_published: true }, authHeaders());
      }
      toast.success(t('builder.publishSuccess'));
      fetchSavedSites();
    } catch (err) { toast.error(t('builder.publishFailed')); }
    finally { setSaving(false); }
  };

  const loadSavedSite = (site) => {
    setSiteConfig(site.config);
    setSiteId(site.id);
    setPreviewToken(site.preview_token || null);
    setSelectedTemplate(site.template_id || 'modern');
    setStep('customize');
    setChatMessages([{ role: 'assistant', content: t('builder.siteLoaded') }]);
  };

  /* ═══ SHARE LIVE PREVIEW ═══ */
  const handleSharePreview = async () => {
    try {
      let id = siteId;
      if (!id) {
        const res = await axios.post(`${API}/builder/sites`, {
          name: siteConfig.restaurant_name || 'My Site',
          branch_id: activeBranch?.id || '',
          template_id: selectedTemplate,
          config: siteConfig,
          is_published: false
        }, authHeaders());
        id = res.data.id;
        setSiteId(id);
        setPreviewToken(res.data.preview_token);
        fetchSavedSites();
      } else {
        await axios.put(`${API}/builder/sites/${id}`, {
          name: siteConfig.restaurant_name || 'My Site',
          branch_id: activeBranch?.id || '',
          config: siteConfig,
          is_published: siteConfig?.is_published || false
        }, authHeaders());
      }

      let token = previewToken;
      if (!token) {
        const tokenRes = await axios.post(`${API}/builder/sites/${id}/preview-token`, {}, authHeaders());
        token = tokenRes.data.preview_token;
        setPreviewToken(token);
      }

      const previewUrl = `${window.location.origin}/preview/${token}`;
      await navigator.clipboard.writeText(previewUrl);
      toast.success('تم نسخ رابط المعاينة!');
    } catch {
      toast.error('فشل إنشاء رابط المعاينة');
    }
  };

  const updateColor = (key, value) => setSiteConfig(prev => ({ ...prev, colors: { ...prev.colors, [key]: value } }));
  const updateFont = (key, value) => setSiteConfig(prev => ({ ...prev, fonts: { ...prev.fonts, [key]: value } }));

  /* ═══ QUICK STYLE FUNCTIONS ═══ */
  const toggleDarkLight = () => {
    if (!siteConfig) return;
    const c = siteConfig.colors || {};
    const isDark = c.bg && (parseInt(c.bg.slice(1, 3), 16) * 299 + parseInt(c.bg.slice(3, 5), 16) * 587 + parseInt(c.bg.slice(5, 7), 16) * 114) / 1000 < 128;
    if (isDark) {
      setSiteConfig(prev => ({ ...prev, colors: { ...prev.colors, bg: '#FFFFFF', bg_section: '#FAFAF9', text: '#1C1917', text_light: '#78716C', card_bg: '#FFFFFF', nav_bg: '#FFFFFF' } }));
    } else {
      setSiteConfig(prev => ({ ...prev, colors: { ...prev.colors, bg: '#0C0C0C', bg_section: '#141414', text: '#F5F5F4', text_light: '#A8A29E', card_bg: '#1A1A1A', nav_bg: '#0C0C0C' } }));
    }
  };

  const setCornerStyle = (style) => {
    const radii = { sharp: '4px', rounded: '16px', pill: '24px' };
    setSiteConfig(prev => ({ ...prev, border_radius: radii[style] || '16px' }));
  };

  const currentIsDark = siteConfig?.colors?.bg && (parseInt(siteConfig.colors.bg.slice(1, 3), 16) * 299 + parseInt(siteConfig.colors.bg.slice(3, 5), 16) * 587 + parseInt(siteConfig.colors.bg.slice(5, 7), 16) * 114) / 1000 < 128;

  // ========= GENERATION OVERLAY =========
  if (generationPhase) {
    return (
      <>
        <ModuleLayout moduleKey="ai"><div /></ModuleLayout>
        <GenerationProgress phase={generationPhase} />
      </>
    );
  }

  // ========= START STEP =========
  if (step === 'start') {
    return (
      <ModuleLayout moduleKey="ai">
        <div className="space-y-6" data-testid="ai-builder-page">
          <div>
            <h1 className="font-cairo font-bold text-2xl text-foreground flex items-center gap-2">
              <Wand2 className="w-6 h-6 text-primary" /> {t('builder.title')}
            </h1>
            <p className="text-sm text-muted-foreground">{t('builder.subtitle')}</p>
          </div>

          {/* ═══ ONE-CLICK GENERATION ═══ */}
          {builderContext && (builderContext.has_menu || builderContext.branch_name) && (
            <Card className="border-2 border-primary/30 rounded-2xl overflow-hidden bg-gradient-to-l from-primary/5 via-transparent to-transparent" data-testid="one-click-card">
              <CardContent className="p-6">
                <div className="flex items-center justify-between gap-4">
                  <div className="flex-1" dir="rtl">
                    <div className="flex items-center gap-2 mb-2">
                      <Sparkles className="w-5 h-5 text-primary" />
                      <h3 className="font-cairo font-bold text-lg text-foreground">أنشئ موقعك بضغطة واحدة</h3>
                    </div>
                    <p className="text-sm text-muted-foreground mb-1">سنستخدم بيانات <span className="text-primary font-semibold">{builderContext.branch_name}</span> لإنشاء موقع احترافي تلقائياً</p>
                    <div className="flex flex-wrap gap-2 mt-3 text-xs text-muted-foreground">
                      {builderContext.has_menu && <span className="px-2 py-0.5 rounded-full bg-primary/10 text-primary">{builderContext.menu_items_count} صنف</span>}
                      <span className="px-2 py-0.5 rounded-full bg-muted">{builderContext.branch_address || ''}</span>
                    </div>
                  </div>
                  <Button onClick={handleOneClickGenerate} disabled={generating} className="h-14 px-8 rounded-2xl text-base font-bold gap-2 shrink-0" data-testid="one-click-generate-btn">
                    <Wand2 className="w-5 h-5" /> أنشئ موقعي
                  </Button>
                </div>
              </CardContent>
            </Card>
          )}

          {/* Saved Sites */}
          {savedSites.length > 0 && (
            <div>
              <h3 className="font-cairo font-semibold text-sm text-foreground mb-3">{t('builder.savedSites')}</h3>
              <div className="flex gap-3 overflow-x-auto pb-2">
                {savedSites.map(site => (
                  <Card key={site.id} className="min-w-[200px] bg-card border border-border/50 rounded-xl cursor-pointer hover:border-primary/50 transition-colors" onClick={() => loadSavedSite(site)} data-testid={`saved-site-${site.id}`}>
                    <CardContent className="p-4">
                      <p className="font-semibold text-sm text-foreground truncate">{site.name}</p>
                      <p className="text-xs text-muted-foreground mt-1">{site.is_published ? t('builder.published') : t('builder.draft')}</p>
                    </CardContent>
                  </Card>
                ))}
              </div>
            </div>
          )}

          {/* Template Gallery */}
          <div>
            <h3 className="font-cairo font-semibold text-sm text-foreground mb-2">{t('builder.chooseTemplate')}</h3>
            {builderContext?.business_type && (
              <p className="text-xs text-muted-foreground mb-3">
                {getTemplateCategory(builderContext.business_type) === 'cafe' ? 'القوالب المناسبة للكافيهات تظهر أولاً' : getTemplateCategory(builderContext.business_type) === 'food_truck' ? 'القوالب المناسبة لعربات الطعام تظهر أولاً' : 'القوالب المناسبة للمطاعم تظهر أولاً'}
              </p>
            )}
            <div className="grid grid-cols-2 lg:grid-cols-3 gap-4">
              {sortedTemplates.map(tmpl => (
                <Card key={tmpl.id} className={`bg-card border-2 rounded-xl cursor-pointer transition-all hover:shadow-md ${selectedTemplate === tmpl.id ? 'border-primary shadow-md' : 'border-border/50'}`} onClick={() => setSelectedTemplate(tmpl.id)} data-testid={`template-${tmpl.id}`}>
                  <CardContent className="p-4">
                    <div className="h-20 rounded-lg mb-3 overflow-hidden relative" style={{ background: `linear-gradient(135deg, ${tmpl.colors?.bg || '#fff'} 60%, ${tmpl.colors?.primary || '#D97706'}30)` }}>
                      <div className="absolute inset-0 flex items-center justify-center">
                        <div className="w-16 h-10 rounded" style={{ backgroundColor: tmpl.colors?.primary || '#D97706', opacity: 0.2 }} />
                      </div>
                      <div className="absolute bottom-0 left-0 right-0 h-6" style={{ background: `linear-gradient(to top, ${tmpl.colors?.bg || '#fff'}80, transparent)` }} />
                    </div>
                    <div className="flex items-center justify-between mb-1">
                      <h4 className="font-cairo font-semibold text-sm text-foreground">{tmpl.name_ar}</h4>
                      <span className="text-[10px] px-2 py-0.5 rounded-full bg-muted text-muted-foreground">{tmpl.business_type === 'cafe' ? 'كافيه' : tmpl.business_type === 'food_truck' ? 'عربة' : 'مطعم'}</span>
                    </div>
                    <p className="text-xs text-muted-foreground">{tmpl.description_ar}</p>
                    {selectedTemplate === tmpl.id && (
                      <div className="mt-2 flex items-center gap-1 text-primary text-xs"><Check className="w-3 h-3" /> {t('builder.selected')}</div>
                    )}
                  </CardContent>
                </Card>
              ))}
            </div>
          </div>

          {/* Or: Custom Description */}
          <div className="relative">
            <div className="absolute inset-0 flex items-center"><div className="w-full border-t border-border" /></div>
            <div className="relative flex justify-center"><span className="bg-background px-4 text-xs text-muted-foreground">أو صف موقعك بنفسك</span></div>
          </div>

          <Tabs defaultValue="describe" className="w-full">
            <TabsList className="grid w-full grid-cols-3" data-testid="builder-input-tabs">
              <TabsTrigger value="describe">{t('builder.textDescription')}</TabsTrigger>
              <TabsTrigger value="url">{t('builder.cloneSite')}</TabsTrigger>
              <TabsTrigger value="images">{t('builder.uploadImages')}</TabsTrigger>
            </TabsList>

            <TabsContent value="describe" className="mt-4">
              <Card className="bg-card border border-border/50 rounded-xl">
                <CardContent className="p-5 space-y-4">
                  <div>
                    <Label className="text-sm mb-2 block">{t('builder.describeDesign')}</Label>
                    <Textarea value={prompt} onChange={e => setPrompt(e.target.value)} placeholder={t('builder.promptPlaceholder')} rows={3} className="text-sm" data-testid="builder-prompt-input" />
                  </div>
                  <div className="flex flex-wrap gap-2">
                    {[t('builder.promptExample1'), t('builder.promptExample2'), t('builder.promptExample3'), t('builder.promptExample4')].map((ex, i) => (
                      <button key={i} onClick={() => setPrompt(ex)} className="text-xs px-3 py-1.5 rounded-full bg-muted text-muted-foreground hover:bg-primary/10 hover:text-primary transition-colors" data-testid={`example-${i}`}>{ex}</button>
                    ))}
                  </div>
                </CardContent>
              </Card>
            </TabsContent>

            <TabsContent value="url" className="mt-4">
              <Card className="bg-card border border-border/50 rounded-xl">
                <CardContent className="p-5 space-y-4">
                  {/* Step 1: Paste URL */}
                  <div>
                    <Label className="text-sm mb-2 block font-semibold">الصق رابط موقع المنافس</Label>
                    <div className="flex gap-2">
                      <Input value={urlInput} onChange={e => { setUrlInput(e.target.value); setCloneAnalysis(null); }} placeholder="https://competitor-restaurant.com" className="flex-1" dir="ltr" data-testid="builder-url-input" />
                      <Button onClick={handleAnalyzeUrl} disabled={analyzing} data-testid="analyze-url-btn" className="rounded-full gap-1.5">
                        {analyzing ? <Loader2 className="w-4 h-4 animate-spin" /> : <><Link2 className="w-4 h-4" /> تحليل</>}
                      </Button>
                    </div>
                  </div>

                  {/* Step 2: Show Analysis Results */}
                  {cloneAnalysis && (
                    <div className="space-y-3 p-4 bg-muted/30 rounded-xl border border-border/50" data-testid="clone-analysis-result">
                      <div className="flex items-center gap-2 mb-1">
                        <CheckCircle2 className="w-4 h-4 text-green-500" />
                        <span className="text-sm font-semibold text-foreground">تم تحليل الموقع</span>
                      </div>

                      {/* Title & Description */}
                      {cloneAnalysis.title && (
                        <div className="text-xs">
                          <span className="text-muted-foreground">العنوان: </span>
                          <span className="text-foreground font-medium">{cloneAnalysis.title}</span>
                        </div>
                      )}
                      {cloneAnalysis.description && (
                        <p className="text-xs text-muted-foreground line-clamp-2">{cloneAnalysis.description}</p>
                      )}

                      {/* Extracted Colors */}
                      {cloneAnalysis.extracted_colors?.length > 0 && (
                        <div>
                          <div className="flex items-center gap-1.5 mb-1.5">
                            <Palette className="w-3 h-3 text-muted-foreground" />
                            <span className="text-xs text-muted-foreground">الألوان المستخرجة</span>
                          </div>
                          <div className="flex gap-1.5 flex-wrap">
                            {cloneAnalysis.extracted_colors.slice(0, 8).map((color, i) => (
                              <div key={i} className="flex items-center gap-1 px-2 py-0.5 rounded-full bg-background border text-[10px] font-mono" data-testid={`extracted-color-${i}`}>
                                <div className="w-3 h-3 rounded-full border" style={{ backgroundColor: color }} />
                                {color}
                              </div>
                            ))}
                          </div>
                        </div>
                      )}

                      {/* Extracted Fonts */}
                      {cloneAnalysis.extracted_fonts?.length > 0 && (
                        <div>
                          <div className="flex items-center gap-1.5 mb-1.5">
                            <Type className="w-3 h-3 text-muted-foreground" />
                            <span className="text-xs text-muted-foreground">الخطوط المستخرجة</span>
                          </div>
                          <div className="flex gap-1.5 flex-wrap">
                            {cloneAnalysis.extracted_fonts.map((font, i) => (
                              <span key={i} className="text-[10px] px-2 py-0.5 rounded-full bg-background border">{font}</span>
                            ))}
                          </div>
                        </div>
                      )}

                      {/* Extracted Images */}
                      {cloneAnalysis.extracted_images?.length > 0 && (
                        <div>
                          <div className="flex items-center gap-1.5 mb-1.5">
                            <ImageIcon className="w-3 h-3 text-muted-foreground" />
                            <span className="text-xs text-muted-foreground">{cloneAnalysis.extracted_images.length} صورة مستخرجة</span>
                          </div>
                          <div className="flex gap-2 overflow-x-auto pb-1">
                            {cloneAnalysis.extracted_images.slice(0, 4).map((img, i) => (
                              <img key={i} src={img} alt="" className="w-16 h-12 rounded object-cover border flex-shrink-0" onError={e => e.target.style.display = 'none'} />
                            ))}
                          </div>
                        </div>
                      )}

                      {/* Clone Button */}
                      <Button onClick={handleCloneGenerate} disabled={generating} className="w-full h-11 rounded-full text-sm font-bold gap-2 mt-2" data-testid="clone-generate-btn">
                        {generating ? <><Loader2 className="w-4 h-4 animate-spin" /> جاري الاستنساخ...</> : <><Copy className="w-4 h-4" /> استنسخ هذا التصميم لموقعي</>}
                      </Button>
                    </div>
                  )}

                  {/* Additional instructions */}
                  {cloneAnalysis && (
                    <div>
                      <Label className="text-xs mb-1.5 block text-muted-foreground">ملاحظات إضافية (اختياري)</Label>
                      <Textarea value={prompt} onChange={e => setPrompt(e.target.value)} placeholder="مثال: أريد نفس الألوان لكن بخط مختلف..." rows={2} className="text-sm" />
                    </div>
                  )}
                </CardContent>
              </Card>
            </TabsContent>

            <TabsContent value="images" className="mt-4">
              <Card className="bg-card border border-border/50 rounded-xl">
                <CardContent className="p-5 space-y-4">
                  <input ref={fileInputRef} type="file" accept="image/*" multiple onChange={handleImageUpload} className="hidden" />
                  <Button variant="outline" onClick={() => fileInputRef.current?.click()} className="w-full h-24 border-dashed border-2 rounded-xl" data-testid="upload-images-btn">
                    <div className="text-center">
                      <ImagePlus className="w-6 h-6 mx-auto mb-2 text-muted-foreground" />
                      <p className="text-sm text-muted-foreground">{t('builder.clickToUpload')}</p>
                    </div>
                  </Button>
                  {uploadedImages.length > 0 && (
                    <div className="grid grid-cols-3 gap-3">
                      {uploadedImages.map((img, i) => (
                        <div key={i} className="relative group">
                          <img src={img.fullUrl || `${process.env.REACT_APP_BACKEND_URL}${img.url}`} alt={img.filename} className="w-full h-20 object-cover rounded-lg" />
                          <button onClick={() => removeImage(i)} className="absolute top-1 right-1 w-5 h-5 bg-destructive text-white rounded-full flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity">
                            <Trash2 className="w-3 h-3" />
                          </button>
                        </div>
                      ))}
                    </div>
                  )}
                  <Textarea value={prompt} onChange={e => setPrompt(e.target.value)} placeholder={t('builder.describePrompt')} rows={2} className="text-sm" />
                </CardContent>
              </Card>
            </TabsContent>
          </Tabs>

          <Button onClick={() => handleGenerate()} disabled={generating} className="w-full h-12 rounded-full text-base font-semibold" data-testid="builder-generate-btn">
            {generating ? <><Loader2 className="w-5 h-5 animate-spin mr-2" />{t('builder.generating')}</> : <><Wand2 className="w-5 h-5 mr-2" />{t('builder.generateSite')}</>}
          </Button>
        </div>
      </ModuleLayout>
    );
  }

  // ========= CUSTOMIZE STEP =========
  return (
    <ModuleLayout moduleKey="ai">
      <div className="space-y-3" data-testid="builder-customize">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Button variant="ghost" size="sm" onClick={() => setStep('start')} data-testid="builder-back-btn" className="h-8 w-8 p-0">
              <ChevronRight className="w-4 h-4" />
            </Button>
            <div>
              <h1 className="font-cairo font-bold text-lg text-foreground">{t('builder.customizeSite')}</h1>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <Button variant="outline" size="sm" onClick={handleSharePreview} data-testid="share-preview-btn" className="rounded-full gap-1 border-primary/30 text-primary hover:bg-primary/10">
              <Link2 className="w-3.5 h-3.5" /> شارك المعاينة
            </Button>
            <Button variant="outline" size="sm" onClick={() => setPreviewFullscreen(true)} data-testid="preview-fullscreen-btn" className="rounded-full gap-1">
              <Eye className="w-3.5 h-3.5" /> {t('builder.preview')}
            </Button>
            <Button variant="outline" size="sm" onClick={handleSave} disabled={saving} data-testid="builder-save-btn" className="rounded-full gap-1">
              {saving ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Save className="w-3.5 h-3.5" />} {t('builder.save')}
            </Button>
            <Button size="sm" onClick={handlePublish} disabled={saving} data-testid="builder-publish-btn" className="rounded-full gap-1">
              <Globe className="w-3.5 h-3.5" /> {t('builder.publish')}
            </Button>
            {siteId && (
              <Button variant="outline" size="sm" asChild className="rounded-full gap-1" data-testid="view-conv-page-btn">
                <a href={`/site/${siteId}`} target="_blank" rel="noopener noreferrer"><ExternalLink className="w-3.5 h-3.5" /> {t('builder.conversational')}</a>
              </Button>
            )}
          </div>
        </div>

        {/* Main Layout */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-3" style={{ height: 'calc(100vh - 160px)' }}>
          {/* Left Panel */}
          <div className="lg:col-span-1 overflow-y-auto custom-scrollbar space-y-0">
            <Tabs defaultValue="chat" className="w-full">
              <TabsList className="grid w-full grid-cols-3 mb-3">
                <TabsTrigger value="chat">{t('builder.aiChat')}</TabsTrigger>
                <TabsTrigger value="colors">{t('builder.colors')}</TabsTrigger>
                <TabsTrigger value="settings">{t('builder.settings')}</TabsTrigger>
              </TabsList>

              {/* AI Chat */}
              <TabsContent value="chat" className="mt-0">
                <Card className="bg-card border border-border/50 rounded-xl" style={{ height: 'calc(100vh - 260px)' }}>
                  <CardContent className="p-0 h-full flex flex-col">
                    <div className="flex-1 overflow-y-auto p-4 space-y-3 custom-scrollbar">
                      {chatMessages.map((msg, i) => (
                        <div key={i} className={`flex ${msg.role === 'user' ? 'justify-start' : 'justify-end'}`}>
                          <div className={`max-w-[85%] px-3 py-2 text-xs leading-relaxed rounded-xl ${msg.role === 'user' ? 'bg-primary text-primary-foreground' : 'bg-muted text-foreground'}`}>
                            {msg.content}
                          </div>
                        </div>
                      ))}
                      {chatLoading && (
                        <div className="flex justify-end"><div className="bg-muted px-3 py-2 rounded-xl"><Loader2 className="w-3 h-3 animate-spin" /></div></div>
                      )}
                      <div ref={chatEndRef} />
                    </div>
                    <div className="p-3 border-t border-border">
                      <div className="flex gap-2">
                        <Input value={chatInput} onChange={e => setChatInput(e.target.value)} onKeyDown={e => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); handleChatModify(); } }} placeholder={t('builder.chatPlaceholder')} className="flex-1 h-9 text-xs" data-testid="builder-chat-input" disabled={chatLoading} />
                        <Button onClick={handleChatModify} disabled={!chatInput.trim() || chatLoading} className="h-9 w-9 p-0 rounded-full" data-testid="builder-chat-send"><Send className="w-3.5 h-3.5" /></Button>
                      </div>
                      <div className="flex flex-wrap gap-1 mt-2">
                        {[t('builder.chatSuggestion1'), t('builder.chatSuggestion2'), t('builder.chatSuggestion3'), t('builder.chatSuggestion4')].map((s, i) => (
                          <button key={i} onClick={() => setChatInput(s)} className="text-[10px] px-2 py-1 rounded-full bg-muted/50 text-muted-foreground hover:bg-primary/10 hover:text-primary transition-colors">{s}</button>
                        ))}
                      </div>
                    </div>
                  </CardContent>
                </Card>
              </TabsContent>

              {/* Colors */}
              <TabsContent value="colors" className="mt-0">
                <Card className="bg-card border border-border/50 rounded-xl overflow-y-auto" style={{ maxHeight: 'calc(100vh - 260px)' }}>
                  <CardContent className="p-4 space-y-4">
                    {siteConfig?.colors && Object.entries(siteConfig.colors).map(([key, val]) => (
                      <div key={key} className="flex items-center justify-between">
                        <Label className="text-xs capitalize">{key.replace('_', ' ')}</Label>
                        <div className="flex items-center gap-2">
                          <input type="color" value={val} onChange={e => updateColor(key, e.target.value)} className="w-8 h-8 rounded cursor-pointer border-0" data-testid={`color-${key}`} />
                          <Input value={val} onChange={e => updateColor(key, e.target.value)} className="w-24 h-8 text-xs font-mono" />
                        </div>
                      </div>
                    ))}
                  </CardContent>
                </Card>
              </TabsContent>

              {/* Settings */}
              <TabsContent value="settings" className="mt-0">
                <Card className="bg-card border border-border/50 rounded-xl overflow-y-auto" style={{ maxHeight: 'calc(100vh - 260px)' }}>
                  <CardContent className="p-4 space-y-4">
                    <div>
                      <Label className="text-xs mb-1 block">{t('builder.headingFont')}</Label>
                      <select value={siteConfig?.fonts?.heading || 'Cairo'} onChange={e => updateFont('heading', e.target.value)} className="w-full h-9 text-xs bg-background border border-input rounded-md px-3" data-testid="font-heading-select">
                        {FONT_OPTIONS.map(f => <option key={f} value={f}>{f}</option>)}
                      </select>
                    </div>
                    <div>
                      <Label className="text-xs mb-1 block">{t('builder.bodyFont')}</Label>
                      <select value={siteConfig?.fonts?.body || 'IBM Plex Sans Arabic'} onChange={e => updateFont('body', e.target.value)} className="w-full h-9 text-xs bg-background border border-input rounded-md px-3" data-testid="font-body-select">
                        {FONT_OPTIONS.map(f => <option key={f} value={f}>{f}</option>)}
                      </select>
                    </div>
                    <div>
                      <Label className="text-xs mb-1 block">{t('builder.heroStyle')}</Label>
                      <select value={siteConfig?.hero_variant || siteConfig?.hero_style || 'split'} onChange={e => setSiteConfig(prev => ({ ...prev, hero_variant: e.target.value }))} className="w-full h-9 text-xs bg-background border border-input rounded-md px-3" data-testid="hero-style-select">
                        <option value="cinematic">سينمائي (Cinematic)</option>
                        <option value="editorial">تحريري (Editorial)</option>
                        <option value="minimal">بسيط (Minimal)</option>
                        <option value="immersive">غامر (Immersive)</option>
                        <option value="split">مقسّم (Split)</option>
                      </select>
                    </div>
                    <div className="flex items-center justify-between">
                      <Label className="text-xs">{t('builder.showChatWidget')}</Label>
                      <Switch checked={siteConfig?.show_chat_widget ?? true} onCheckedChange={val => setSiteConfig(prev => ({ ...prev, show_chat_widget: val }))} data-testid="toggle-chat-widget" />
                    </div>
                    <div>
                      <input ref={fileInputRef} type="file" accept="image/*" multiple onChange={handleImageUpload} className="hidden" />
                      <Button variant="outline" size="sm" onClick={() => fileInputRef.current?.click()} className="w-full h-9 text-xs rounded-full" data-testid="upload-more-images">
                        <ImagePlus className="w-3.5 h-3.5 mr-2" /> {t('builder.uploadImages')}
                      </Button>
                    </div>
                    <Button variant="outline" size="sm" onClick={() => handleGenerate()} disabled={generating} className="w-full rounded-full text-xs" data-testid="regenerate-btn">
                      <RefreshCw className="w-3.5 h-3.5 mr-2" /> {t('builder.regenerate')}
                    </Button>
                  </CardContent>
                </Card>
              </TabsContent>
            </Tabs>
          </div>

          {/* Right Panel: Preview */}
          <div className="lg:col-span-2 border border-border/50 rounded-xl overflow-hidden bg-white" style={{ height: 'calc(100vh - 160px)' }}>
            {/* ═══ BROWSER CHROME WITH DEVICE TOGGLE + QUICK STYLE ═══ */}
            <div className="h-10 bg-muted/50 border-b border-border flex items-center px-3 gap-2">
              <div className="flex gap-1.5">
                <div className="w-2.5 h-2.5 rounded-full bg-destructive/50" />
                <div className="w-2.5 h-2.5 rounded-full bg-yellow-500/50" />
                <div className="w-2.5 h-2.5 rounded-full bg-green-500/50" />
              </div>

              {/* URL bar */}
              <div className="flex-1 mx-4">
                <div className="bg-background/80 rounded-full px-3 py-0.5 text-[10px] text-muted-foreground text-center">
                  {siteConfig?.restaurant_name || 'myrestaurant'}.zouqai.com
                </div>
              </div>

              {/* Quick Style Toggles */}
              <div className="flex items-center gap-1 border-l border-border pl-2 ml-1">
                <button onClick={toggleDarkLight} className={`p-1.5 rounded-md transition-colors ${currentIsDark ? 'bg-muted text-foreground' : 'text-muted-foreground hover:bg-muted'}`} title="داكن / فاتح" data-testid="toggle-dark-light">
                  {currentIsDark ? <Moon className="w-3.5 h-3.5" /> : <Sun className="w-3.5 h-3.5" />}
                </button>
                <button onClick={() => setCornerStyle('sharp')} className={`p-1.5 rounded-md transition-colors ${siteConfig?.border_radius === '4px' ? 'bg-muted text-foreground' : 'text-muted-foreground hover:bg-muted'}`} title="حاد" data-testid="style-sharp">
                  <Square className="w-3.5 h-3.5" />
                </button>
                <button onClick={() => setCornerStyle('pill')} className={`p-1.5 rounded-md transition-colors ${siteConfig?.border_radius === '24px' ? 'bg-muted text-foreground' : 'text-muted-foreground hover:bg-muted'}`} title="دائري" data-testid="style-pill">
                  <Circle className="w-3.5 h-3.5" />
                </button>
              </div>

              {/* Device Toggle */}
              <div className="flex items-center gap-1 border-l border-border pl-2" data-testid="device-toggle">
                <button onClick={() => setPreviewMode('desktop')} className={`p-1.5 rounded-md transition-colors ${previewMode === 'desktop' ? 'bg-muted text-foreground' : 'text-muted-foreground hover:bg-muted'}`} data-testid="device-desktop">
                  <Monitor className="w-3.5 h-3.5" />
                </button>
                <button onClick={() => setPreviewMode('tablet')} className={`p-1.5 rounded-md transition-colors ${previewMode === 'tablet' ? 'bg-muted text-foreground' : 'text-muted-foreground hover:bg-muted'}`} data-testid="device-tablet">
                  <Tablet className="w-3.5 h-3.5" />
                </button>
                <button onClick={() => setPreviewMode('mobile')} className={`p-1.5 rounded-md transition-colors ${previewMode === 'mobile' ? 'bg-muted text-foreground' : 'text-muted-foreground hover:bg-muted'}`} data-testid="device-mobile">
                  <Smartphone className="w-3.5 h-3.5" />
                </button>
              </div>
            </div>

            {/* ═══ RESPONSIVE PREVIEW ═══ */}
            {previewMode === 'mobile' ? (
              <PhoneFrame>
                <SitePreview config={siteConfig} menuItems={menuItems} className="h-full" />
              </PhoneFrame>
            ) : previewMode === 'tablet' ? (
              <div className="flex justify-center h-[calc(100%-40px)] overflow-hidden" style={{ backgroundColor: '#f0f0f0' }}>
                <div style={{ width: 768, height: '100%', boxShadow: '0 0 30px rgba(0,0,0,0.1)' }}>
                  <SitePreview config={siteConfig} menuItems={menuItems} className="h-full" />
                </div>
              </div>
            ) : (
              <SitePreview config={siteConfig} menuItems={menuItems} className="h-[calc(100%-40px)]" />
            )}
          </div>
        </div>
      </div>

      {/* Fullscreen Preview */}
      <Dialog open={previewFullscreen} onOpenChange={setPreviewFullscreen}>
        <DialogContent className="max-w-5xl h-[90vh] p-0 overflow-hidden">
          <div className="h-8 bg-muted/50 border-b border-border flex items-center px-3 gap-2">
            <div className="flex gap-1.5">
              <div className="w-2.5 h-2.5 rounded-full bg-destructive/50" />
              <div className="w-2.5 h-2.5 rounded-full bg-yellow-500/50" />
              <div className="w-2.5 h-2.5 rounded-full bg-green-500/50" />
            </div>
            <div className="flex-1 mx-8">
              <div className="bg-background/80 rounded-full px-3 py-0.5 text-[10px] text-muted-foreground text-center">
                {siteConfig?.restaurant_name || 'myrestaurant'}.zouqai.com
              </div>
            </div>
          </div>
          <SitePreview config={siteConfig} menuItems={menuItems} className="h-[calc(100%-32px)]" />
        </DialogContent>
      </Dialog>
    </ModuleLayout>
  );
}
